import { MutableRefObject } from "react";

import { getAppShortName, getBrandIconUrl } from "../../config/chains";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { ToastEnum, ToastStatusEnum } from "../../utils/toast";
import { ToastLogoContainer, ToastSubheader } from "./style";

export interface RunRegisterParams {
  inviteCode: string;
  inviteRequired: boolean;
  setPageError: (value: string) => void;
  account: string | null | undefined;
  lastRegisterSuccessRef: MutableRefObject<{
    account: string;
    timestamp: number;
  } | null>;
  registerInFlightRef: MutableRefObject<boolean>;
  setLoading: (value: boolean) => void;
  registerSigningKey: (
    isRememberMeChecked: boolean,
    inviteCode?: string
  ) => Promise<{ status: any; message: string }>;
  isRememberMeChecked: boolean;
  addToast: (toast: any, duration?: number) => void;
  referralData: any;
  mutateInviteStatus: () => void;
  t: (key: string, options?: any) => string;
}

// Extracted verbatim from ConnectWalletModal's `register` useCallback body.
export const runRegister = async ({
  inviteCode,
  inviteRequired,
  setPageError,
  account,
  lastRegisterSuccessRef,
  registerInFlightRef,
  setLoading,
  registerSigningKey,
  isRememberMeChecked,
  addToast,
  referralData,
  mutateInviteStatus,
  t,
}: RunRegisterParams) => {
  const normalizedInviteCode = inviteCode.trim();
  if (inviteRequired && !normalizedInviteCode) {
    setPageError("Invite code required");
    return;
  }

  const normalizedAccount = account?.toLowerCase();
  const lastRegisterSuccess = lastRegisterSuccessRef.current;
  if (
    normalizedAccount &&
    lastRegisterSuccess?.account === normalizedAccount &&
    Date.now() - lastRegisterSuccess.timestamp < 15000
  ) {
    return;
  }

  if (registerInFlightRef.current) {
    return;
  }

  registerInFlightRef.current = true;
  setLoading(true);
  try {
    const { status, message } = await registerSigningKey(
      isRememberMeChecked,
      inviteRequired ? normalizedInviteCode : undefined
    );
    addToast(
      {
        type: ToastEnum.SIMPLE,
        header: message,
        status,
      },
      4000
    );

    const referral = window.localStorage.getItem(
      LocalStorageKeyEnum.REFERRAL_CODE
    );

    if (status === ToastStatusEnum.ERROR) {
      setPageError(message);
    } else if (status === ToastStatusEnum.SUCCESS && referral && referralData) {
      if (normalizedAccount) {
        lastRegisterSuccessRef.current = {
          account: normalizedAccount,
          timestamp: Date.now(),
        };
      }
      mutateInviteStatus();
      addToast(
        {
          type: ToastEnum.INFO,
          header: t("trading_fee_discount"),
          status: ToastStatusEnum.SUCCESS,
          icon: (
            <ToastLogoContainer>
              <img src={getBrandIconUrl()} alt={getAppShortName()} />
            </ToastLogoContainer>
          ),
          stats: [
            {
              label: (
                <ToastSubheader>
                  {t("trading_fee_discount_desc", {
                    refereeDiscount: (
                      Number(referralData.referee_discount) * 100
                    ).toFixed(0),
                  })}{" "}
                  <span>{referral}</span>.
                </ToastSubheader>
              ),
              value: "",
            },
          ],
        },
        4000
      );
      window.localStorage.removeItem(LocalStorageKeyEnum.REFERRAL_CODE);
      window.localStorage.removeItem(LocalStorageKeyEnum.ACCESS_CODE);
    } else if (status === ToastStatusEnum.SUCCESS) {
      if (normalizedAccount) {
        lastRegisterSuccessRef.current = {
          account: normalizedAccount,
          timestamp: Date.now(),
        };
      }
      mutateInviteStatus();
      window.localStorage.removeItem(LocalStorageKeyEnum.ACCESS_CODE);
    }

    setLoading(false);

  } finally {
    setLoading(false);
    registerInFlightRef.current = false;
  }
};
